import { BaseApiService } from './BaseApiService';
import i18next from 'i18next';
import { mapRecurse } from './utils';

export class StatisticsService extends BaseApiService {
  modulesMap = {
    expenditure: 'expenditure',
    // contract: 'contract',
    // transfer: 'transfer',
    revenue: 'revenue',
    'delegated-function': 'delegated-function',
    // 'financial-indicator': 'financial-indicator',
    arrears: 'arrears',
  };

  modulesByi18nKey = {
    expenditure: 'STATISTICS.EXPENDITURES',
    // contract: 'STATISTICS.CONTRACTS',
    // transfer: 'STATISTICS.BUDGET_TRANSFERS',
    revenue: 'STATISTICS.REVENUES',
    'delegated-function': 'STATISTICS.DELEGATED_FUNCTION',
    // 'financial-indicator': 'STATISTICS.FINANCIAL_INDICATOR',
    arrears: 'STATISTICS.ARREARS',
  };

  highChartTypeByModule = {
    expenditure: {
      chart: {
        height: 600,
        type: 'column',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          states: {
            hover: {
              brightness: -0.3, // darken
            },
          },
        },
      },
    },

    revenue: {
      chart: {
        height: 600,
        type: 'column',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          states: {
            hover: {
              brightness: -0.3, // darken
            },
          },
        },
      },
    },

    contract: {
      chart: {
        height: 600,
        type: 'column',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          states: {
            hover: {
              brightness: -0.3, // darken
            },
          },
        },
      },
    },

    // transfer: {
    //   chart: {
    //     height: 600,
    //     type: 'column',
    //   },
    //   plotOptions: {
    //     column: {
    //       stacking: 'normal',
    //     },
    //     series: {
    //       states: {
    //         hover: {
    //           brightness: -0.3, // darken
    //         },
    //       },
    //     },
    //   },
    // },

    'delegated-function': {
      chart: {
        height: 600,
        type: 'column',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          states: {
            hover: {
              brightness: -0.3, // darken
            },
          },
        },
      },
    },

    'financial-indicator': {
      chart: {
        height: 600,
        type: 'line',
      },
      plotOptions: {
        series: {
          states: {
            hover: {
              brightness: -0.3, // darken
            },
          },
        },
      },
    },
  };

  filterTypesMap = [
    {
      value: 'municipality',
      description: i18next.t('STATISTICS.BY_MUNICIPALITY'),
      path: 'compare-government-entities',
      generateParams: (filters) => ({
        data_level_id: filters.dataLevels?.length > 0 ? filters.dataLevels[0] : undefined,
        year_id: filters.year?.length > 0 ? filters.year[0] : undefined,
        government_entity_id: filters.municipality,
        formula_id: filters.formulaId,
        period_type: filters.period.type,
        start_month: filters.period.start,
        end_month: filters.period.end,
      }),
    },
    {
      value: 'year',
      description: i18next.t('STATISTICS.BY_PERIODS'),
      path: 'compare-periods',
      generateParams: (filters) => ({
        data_level_id: filters.dataLevels?.length > 0 ? filters.dataLevels[0] : undefined,
        year_id: filters.year,
        government_entity_id: filters.municipality?.length > 0 ? filters.municipality[0] : undefined,
        formula_id: filters.formulaId,
        period_type: filters.period.type,
        start_month: filters.period.start,
        end_month: filters.period.end,
      }),
    },
    {
      value: 'dataLevels',
      description: i18next.t('STATISTICS.DATA_LEVEL'),
      path: 'compare-data-levels',
      generateParams: (filters) => ({
        data_level_id: filters.dataLevels,
        year_id: filters.year?.length > 0 ? filters.year[0] : undefined,
        government_entity_id: filters.municipality?.length > 0 ? filters.municipality[0] : undefined,
        formula_id: filters.formulaId,
        period_type: filters.period.type,
        start_month: filters.period.start,
        end_month: filters.period.end,
      }),
    },
  ];

  constructor() {
    super();
    this.api = this.instance;
  }

  // GET api/expenditure/data-levels&lang_id={lang_id}
  async expenditureDataLevels() {
    return this.api.get('/expenditure/data-levels');
  }

  async getNationalIndicatorChartData(){
    return this.api.get('/national-indicator/statistics');
  }

  async getLocalIndicatorChartData(){
    return this.api.get('/local-indicator/statistics');
  }

  async getStatisticsTableData(){
    return this.api.get('/web/activity/statistic-indicators-data-periods');
  }

  // GET /api/{module_name}/formulas?lang_id={lang_id}
  async getFormulasByModule(moduleName) {
    const response = await this.api.get(`/${moduleName}/formulas`);

    if (!response?.data) return [];

    return mapRecurse((x) => {
      return {
        description: x.title,
        value: x.id,
        children: x.nested_formulas,
      };
    }, response.data);
  }

  // GET api/expenditure/statistics?lang_id=,&government_entity_id=1&year_id=1&period_type=monthly&data_level_id=1&formula_id=1
  async expenditureChartData({ governmentEntityId, dataLevelId, formulaId, yearId, periodType }) {
    return this.api.get('/expenditure/statistics', {
      params: {
        government_entity_id: governmentEntityId,
        data_level_id: dataLevelId,
        formula_id: formulaId,
        year_id: yearId,
        period_type: periodType,
      },
    });
  }

  async getChartData(moduleValue, filterTypeValue, filters) {
    const endpoint = this.generateEndPointByModuleAndFilterType(moduleValue, filterTypeValue);
    const filterType = this.filterTypesMap.find((x) => x.value === filterTypeValue);
    const params = filterType.generateParams(filters);

    return this.api.get(endpoint, { params });
  }

  async periods() {
    const config = [
      {
        value: 'monthly',
        description: i18next.t('STATISTICS.PERIOD.MONTHLY'),
        moduleFormula: 1,
      },
      {
        value: 'bimonthly',
        description: i18next.t('STATISTICS.PERIOD.BIMONTHLY'),
        moduleFormula: 2,
      },
      {
        value: 'quarter',
        description: i18next.t('STATISTICS.PERIOD.QUARTERLY'),
        moduleFormula: 3,
      },
      {
        value: 'four_month',
        description: i18next.t('STATISTICS.PERIOD.FOUR_MONTHLY'),
        moduleFormula: 4,
      },
      {
        value: 'semiannual',
        description: i18next.t('STATISTICS.PERIOD.SEMIANNUALLY'),
        moduleFormula: 6,
      },
      {
        value: 'annual',
        description: i18next.t('STATISTICS.PERIOD.ANNUALLY'),
        moduleFormula: null,
      },
    ];

    return Promise.resolve({
      data: config.map((conf, index) => {
        if (!conf.moduleFormula)
          return {
            ...conf,
            start: 1,
            end: 12,
            type: conf.value,
          };

        return {
          ...conf,
          children: [...Array(12 / conf.moduleFormula).keys()].map((x, i) => {
            const start = i * conf.moduleFormula + 1;
            const end = conf.moduleFormula * (i + 1);

            return {
              value: [conf.value, start, end].join('_'),
              description: start === end ? `${start}` : `${start} - ${end}`,
              start,
              end,
              type: conf.value,
            };
          }),
        };
      }),
    });
  }

  async filterTypes() {
    return Promise.resolve({ data: this.filterTypesMap });
  }

  async years(moduleValue) {
    const { data: items } = await this.api.get([this.modulesMap[moduleValue], 'years'].join('/'));

    if (!items) return { data: [] };

    const data = items.map((item) => ({
      value: item.id,
      description: item.year,
    }));

    return { data };
  }

  async dataLevels(moduleValue, formula_id) {
    const { data: items } = await this.api.get([this.modulesMap[moduleValue], 'data-levels'].join('/'), {
      params: { formula_id },
    });

    if (!items) return { data: [] };

    const data = items.map((item) => ({
      value: item.id,
      description: item.level,
    }));

    return { data };
  }

  async governmentEntities(moduleValue, formula_id) {
    const { data: items } = await this.api.get([this.modulesMap[moduleValue], 'government-entities'].join('/'));

    if (!items) return { data: [] };

    const data = items.map((item) => ({
      value: item.id,
      description: item.entity,
    }));

    return { data };
  }

  async dictionary(moduleValue, formula_id) {
    const { data: items } = await this.api.get(['dictionary', 'terms', this.modulesMap[moduleValue]].join('/'), {
      params: { formula_id },
    });

    if (!items) return { data: [] };

    const data = items.map((item) => ({
      value: item.id,
      term: item.term,
      description: item.meaning,
    }));

    return { data };
  }

  generateEndPointByModuleAndFilterType(moduleValue, filterTypeValue) {
    // checks
    const modulePath = this.modulesMap[moduleValue];
    if (!modulePath) console.error('Module not exists. Module value: ', moduleValue);
    const filterPath = this.filterTypesMap.find((x) => x.value === filterTypeValue)?.path;
    if (!filterPath) console.error('Filter Type not exists. Filter Type value: ', filterTypeValue);
    // end checks

    return [modulePath, 'statistics', filterPath].join('/');
  }

  searchTree(item, formulaId, path = []) {
    if (item.value === formulaId) {
      return item ? { item, path } : null;
    } else if (item.children != null) {
      let result = null;

      for (let i = 0; result == null && i < item.children.length; i++) {
        result = this.searchTree(item.children[i], formulaId, [...path, item]);
      }

      return result;
    }

    return null;
  }
}
